import { useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatDate } from "@/lib/format";

export type AnalyticsDatePreset = "month" | "week" | "year";

export type AnalyticsDateFilter =
  | { mode: AnalyticsDatePreset }
  | { mode: "custom"; start: string; end: string };

const PRESETS: Array<{ mode: AnalyticsDatePreset; label: string }> = [
  { mode: "month", label: "This Month" },
  { mode: "week", label: "This Week" },
  { mode: "year", label: "Full Year" },
];

function toYmd(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

function parseYmd(value: string): Date | undefined {
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function presetLabel(mode: AnalyticsDatePreset): string {
  return PRESETS.find((p) => p.mode === mode)?.label ?? "This Month";
}

type AnalyticsDateRangePickerProps = {
  value: AnalyticsDateFilter;
  onChange: (value: AnalyticsDateFilter) => void;
};

export function AnalyticsDateRangePicker({
  value,
  onChange,
}: AnalyticsDateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>();

  const selectedRange = useMemo<DateRange | undefined>(() => {
    if (value.mode !== "custom") return undefined;
    return {
      from: parseYmd(value.start),
      to: parseYmd(value.end),
    };
  }, [value]);

  const triggerLabel =
    value.mode === "custom"
      ? `${formatDate(value.start, { month: "short" })} – ${formatDate(value.end, { month: "short" })}`
      : presetLabel(value.mode);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setDraftRange(undefined);
    }
  };

  const handlePreset = (mode: AnalyticsDatePreset) => {
    onChange({ mode });
    setDraftRange(undefined);
    setOpen(false);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setDraftRange(range);
    if (!range?.from || !range.to) return;
    onChange({
      mode: "custom",
      start: toYmd(range.from),
      end: toYmd(range.to),
    });
    setDraftRange(undefined);
    setOpen(false);
  };

  const calendarSelected = draftRange ?? selectedRange;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button type="button" className="pr-date-trigger">
          <CalendarIcon className="pr-date-trigger-icon" aria-hidden />
          <span>{triggerLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="pr-date-popover">
          <div className="pr-date-presets">
            {PRESETS.map((preset) => (
              <button
                key={preset.mode}
                type="button"
                className={`pr-date-preset ${value.mode === preset.mode ? "active" : ""}`}
                onClick={() => handlePreset(preset.mode)}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <Calendar
            autoFocus
            captionLayout="dropdown"
            mode="range"
            defaultMonth={calendarSelected?.from ?? new Date()}
            selected={calendarSelected}
            onSelect={handleRangeSelect}
            numberOfMonths={2}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
